import {
  listDocs,
  getDoc,
  createDoc,
  updateDoc,
  deleteDoc,
  runQuery
} from './appDataClient';
import { pollingSubscription } from './adapters';
import { getQueryTemplate, executeQuery } from './dataQuery';

// Dashboards and their chart configs are stored through /api/app-data. A chart
// config records its dashboard as parentId, so deleting a dashboard cascades.

const DASHBOARDS = 'dashboards';
const CHARTS = 'chartConfigs';

// ===== Dashboard CRUD =====

export const createDashboard = async (dashboardData) =>
  createDoc(DASHBOARDS, dashboardData);

export const getDashboard = async (dashboardId) => getDoc(DASHBOARDS, dashboardId);

export const getAccountDashboards = async (accountId) =>
  listDocs(DASHBOARDS, { accountId });

export const getAllDashboards = async () => listDocs(DASHBOARDS);

export const updateDashboard = async (dashboardId, updates) =>
  updateDoc(DASHBOARDS, dashboardId, updates);

// The API removes child documents alongside the parent, so the chart configs
// belonging to this dashboard go with it.
export const deleteDashboard = async (dashboardId) => deleteDoc(DASHBOARDS, dashboardId);

// ===== Chart config CRUD =====

export const createChartConfig = async (chartData) =>
  createDoc(CHARTS, { ...chartData, parentId: chartData.dashboardId || chartData.parentId || null });

export const getChartConfig = async (chartId) => getDoc(CHARTS, chartId);

export const getDashboardCharts = async (dashboardId) =>
  listDocs(CHARTS, { parentId: dashboardId });

export const updateChartConfig = async (chartId, updates) =>
  updateDoc(CHARTS, chartId, updates);

export const deleteChartConfig = async (chartId) => deleteDoc(CHARTS, chartId);

// ===== Chart data =====

export const fetchChartData = async (chartConfig) => {
  try {
    const { dataSource, filters, sort, limit } = chartConfig;

    if (dataSource.type === 'custom' && dataSource.customData) {
      return dataSource.customData;
    }

    if (dataSource.type === 'query' && dataSource.queryTemplateId) {
      const template = await getQueryTemplate(dataSource.queryTemplateId);
      if (!template) return [];
      const result = await executeQuery(template);
      return result.results;
    }

    if (dataSource.type === 'collection' && dataSource.collection) {
      const response = await runQuery({
        collection: dataSource.collection,
        conditions: filters || [],
        orderBy: sort ? [sort] : [],
        limit
      });
      return response.results;
    }

    return [];
  } catch (error) {
    console.error('Error fetching chart data:', error);
    throw error;
  }
};

// ===== Subscriptions =====

export const subscribeToDashboards = (accountId, callback) =>
  pollingSubscription(() => getAccountDashboards(accountId), callback);

export const subscribeToDashboard = (dashboardId, callback) =>
  pollingSubscription(() => getDashboard(dashboardId), callback);

export const getChartTypes = () => {
  return [
    { value: 'bar', label: '長條圖', icon: '📊' },
    { value: 'line', label: '折線圖', icon: '📈' },
    { value: 'pie', label: '圓餅圖', icon: '🥧' },
    { value: 'area', label: '面積圖', icon: '📉' },
    { value: 'scatter', label: '散點圖', icon: '⚫' },
    { value: 'table', label: '表格', icon: '📋' },
    { value: 'stat', label: '統計卡片', icon: '🔢' }
  ];
};

export const getDefaultChartColors = () => {
  return [
    '#667eea',
    '#764ba2',
    '#f093fb',
    '#4facfe',
    '#00f2fe',
    '#43e97b',
    '#fa709a',
    '#fee140',
    '#30cfd0',
    '#a8edea'
  ];
};

export const exportDashboardConfig = (dashboard, charts) => {
  const config = {
    dashboard: {
      name: dashboard.name,
      description: dashboard.description,
      layout: dashboard.layout,
      refreshInterval: dashboard.refreshInterval
    },
    charts: charts.map(chart => ({
      name: chart.name,
      type: chart.type,
      dataSource: chart.dataSource,
      config: chart.config,
      filters: chart.filters,
      sort: chart.sort,
      limit: chart.limit
    }))
  };

  const blob = new Blob([JSON.stringify(config, null, 2)], {
    type: 'application/json'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dashboard_${dashboard.name}_${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

export const importDashboardConfig = async (file, accountId) => {
  try {
    const text = await file.text();
    const config = JSON.parse(text);

    // Create dashboard
    const dashboard = await createDashboard({
      ...config.dashboard,
      accountId
    });

    // Create charts
    const charts = [];
    for (const chartConfig of config.charts) {
      const chart = await createChartConfig({
        ...chartConfig,
        dashboardId: dashboard.id
      });
      charts.push(chart);
    }

    return { dashboard, charts };
  } catch (error) {
    console.error('Error importing dashboard config:', error);
    throw error;
  }
};

export const duplicateDashboard = async (dashboardId, accountId) => {
  try {
    const dashboard = await getDashboard(dashboardId);
    const charts = await getDashboardCharts(dashboardId);

    // Create new dashboard
    const newDashboard = await createDashboard({
      ...dashboard,
      name: `${dashboard.name} (複製)`,
      accountId
    });

    // Duplicate charts
    const chartIdMap = {};
    for (const chart of charts) {
      const newChart = await createChartConfig({
        ...chart,
        dashboardId: newDashboard.id
      });
      chartIdMap[chart.id] = newChart.id;
    }

    // Update layout with new chart IDs
    const newLayout = dashboard.layout.map(item => ({
      ...item,
      chartId: chartIdMap[item.chartId] || item.chartId
    }));

    await updateDashboard(newDashboard.id, { layout: newLayout });

    return newDashboard;
  } catch (error) {
    console.error('Error duplicating dashboard:', error);
    throw error;
  }
};

export const calculateStatistics = (data, field) => {
  if (!data || data.length === 0) {
    return { count: 0, sum: 0, avg: 0, min: 0, max: 0 };
  }

  const values = data
    .map(item => Number(item[field]))
    .filter(val => !isNaN(val));

  if (values.length === 0) {
    return { count: data.length, sum: 0, avg: 0, min: 0, max: 0 };
  }

  const sum = values.reduce((acc, val) => acc + val, 0);
  const avg = sum / values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);

  return {
    count: data.length,
    sum: sum.toFixed(2),
    avg: avg.toFixed(2),
    min: min.toFixed(2),
    max: max.toFixed(2)
  };
};
